import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { api } from '@/lib/api';
import type * as API from '@/types/api';
import { useAccountStore } from '@nimbitlabs/features';

interface ServiceEnvironmentsState {
  serviceEnvironments: API.ServiceEnvironmentDto[];
  loading: boolean;
  error: string | null;
  
  fetchServiceEnvironments: () => Promise<void>;
  getByServiceId: (serviceId: string) => API.ServiceEnvironmentDto[];
  createServiceEnvironment: (data: API.CreateServiceEnvironmentDto) => Promise<API.ServiceEnvironmentDto>;
  updateEndpoints: (id: string, data: API.UpdateServiceEnvironmentEndpointsDto) => Promise<void>;
  deleteServiceEnvironment: (id: string) => Promise<void>;
  clearError: () => void;
}

const serviceEnvironmentsStore = create<ServiceEnvironmentsState>()(
  subscribeWithSelector((set, get) => ({
  serviceEnvironments: [],
  loading: false,
  error: null,
  
  fetchServiceEnvironments: async () => {
    set({ loading: true, error: null });
    try {
      const data = await api.serviceEnvironments.getAll();
      set({ serviceEnvironments: data, loading: false });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch service environments', loading: false });
    }
  },
  
  getByServiceId: (serviceId: string) => {
    return get().serviceEnvironments.filter(se => se.serviceId === serviceId);
  },
  
  createServiceEnvironment: async (data: API.CreateServiceEnvironmentDto) => {
    try {
      const newServiceEnvironment = await api.serviceEnvironments.create(data);
      set(state => ({
        serviceEnvironments: [...state.serviceEnvironments, newServiceEnvironment],
      }));
      return newServiceEnvironment;
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to create service environment' });
      throw e;
    }
  },
  
  updateEndpoints: async (id: string, data: API.UpdateServiceEnvironmentEndpointsDto) => {
    const original = get().serviceEnvironments.find(se => se.id === id);
    
    // Optimistically update local state
    set(state => ({
      serviceEnvironments: state.serviceEnvironments.map(se =>
        se.id === id ? { ...se, endpoints: data.endpoints, updatedAt: new Date().toISOString() } : se
      )
    }));
    
    try {
      const updated = await api.serviceEnvironments.updateEndpoints(id, data);
      set(state => ({
        serviceEnvironments: state.serviceEnvironments.map(se => se.id === id ? updated : se)
      }));
    } catch (e: any) {
      // Revert on error
      if (original) {
        set(state => ({
          serviceEnvironments: state.serviceEnvironments.map(se => se.id === id ? original : se)
        }));
      }
      set({ error: e?.message ?? 'Failed to update endpoints' });
      throw e;
    }
  },
  
  deleteServiceEnvironment: async (id: string) => {
    try {
      await api.serviceEnvironments.delete(id);
      set(state => ({
        serviceEnvironments: state.serviceEnvironments.filter(se => se.id !== id),
      }));
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to delete service environment' });
      throw e;
    }
  },
  
  clearError: () => set({ error: null }),
  }))
);

export const useServiceEnvironmentsStore = serviceEnvironmentsStore;

// Subscribe to account changes and auto-fetch
useAccountStore.subscribe(
  (state) => state.currentAccount,
  (currentAccount) => {
    if (currentAccount?.id) {
      serviceEnvironmentsStore.getState().fetchServiceEnvironments();
    } else {
      serviceEnvironmentsStore.setState({ serviceEnvironments: [] });
    }
  }
);
